/**
 * Capture beforeinstallprompt early so Install buttons can trigger
 * the browser's native Install UI (Chrome/Edge: same as ⋮ → Install app).
 *
 * Important: prompt.prompt() must run in the same user-gesture turn.
 * Do not await long work (SW install, network) before calling it.
 */

let deferredPrompt = null;
const listeners = new Set();

function notify() {
  listeners.forEach((fn) => {
    try {
      fn(deferredPrompt);
    } catch {
      /* ignore */
    }
  });
}

export function initPwaInstallCapture() {
  if (typeof window === "undefined") return;
  if (window.__smartttPwaInstallCapture) return;
  window.__smartttPwaInstallCapture = true;

  window.addEventListener("beforeinstallprompt", (event) => {
    // Do not preventDefault: Chrome logs "Banner not shown" if we swallow it.
    deferredPrompt = event;
    try {
      window.__smartttDeferredInstall = event;
    } catch {
      /* ignore */
    }
    notify();
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    try {
      window.__smartttDeferredInstall = null;
    } catch {
      /* ignore */
    }
    notify();
  });
}

export function getDeferredInstallPrompt() {
  return deferredPrompt;
}

export function clearDeferredInstallPrompt() {
  deferredPrompt = null;
  notify();
}

export function subscribeInstallPrompt(listener) {
  listeners.add(listener);
  listener(deferredPrompt);
  return () => listeners.delete(listener);
}

/**
 * Open the browser native install dialog (Chrome/Edge Install app sheet).
 * Must be called directly from a user gesture (button click) with no long awaits before it.
 */
export async function promptNativeInstall() {
  initPwaInstallCapture();
  const prompt = deferredPrompt;
  if (!prompt || typeof prompt.prompt !== "function") {
    return { outcome: "unavailable" };
  }
  try {
    // Call synchronously at the start of the gesture turn.
    const promptPromise = prompt.prompt();
    await promptPromise;
    const choice = await prompt.userChoice;
    clearDeferredInstallPrompt();
    return { outcome: choice?.outcome === "accepted" ? "accepted" : "dismissed" };
  } catch {
    return { outcome: "unavailable" };
  }
}

export function isRunningAsInstalledPwa() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  if (window.matchMedia?.("(display-mode: fullscreen)").matches) return true;
  if (typeof navigator !== "undefined" && navigator.standalone === true) {
    return true;
  }
  return false;
}

export function isIosDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const iPadOs =
    navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOS || iPadOs;
}

export function isChromiumInstallBrowser() {
  if (typeof navigator === "undefined") return false;
  if (isIosDevice()) return false;
  const ua = navigator.userAgent || "";
  // Chrome/Edge/Samsung Internet on Android — not in-app browsers.
  if (/; wv\)/i.test(ua)) return false; // Android WebView
  if (/FBAN|FBAV|Instagram|Line\//i.test(ua)) return false;
  return /Chrome|Chromium|Edg|SamsungBrowser/i.test(ua);
}

/** Chromium browsers that support beforeinstallprompt. */
export function supportsNativeInstallPrompt() {
  if (typeof window === "undefined") return false;
  return isChromiumInstallBrowser();
}

/**
 * Detect an already-installed web app (Android Chrome) via related_applications.
 */
export async function detectInstalledRelatedWebApp() {
  if (typeof navigator === "undefined") return false;
  if (typeof navigator.getInstalledRelatedApps !== "function") return false;
  try {
    const apps = await navigator.getInstalledRelatedApps();
    return Array.isArray(apps) && apps.length > 0;
  } catch {
    return false;
  }
}
